// Workflow graph model for the Builder CMS platform (Phase 0, ENG-169).
//
// A workflow is a directed graph of TYPED NODES joined by UNCONDITIONAL edges.
// All routing lives in the nodes; an edge is a plain wire from one output port
// to one target, optionally carrying session actions that fire when traversed.
//
// This inversion is the point: in the v2 card model a card carried its own
// `branches[]` and `next_card_id`, so "no branch matched and next_card_id was
// null" silently ended the tutorial. Here, every output port must be wired
// (validated at publish), so silent success is structurally impossible.
//
// These types are the wire contract shared by the web player, the v1 API, and
// the Swift mirror on iOS — keep them serialisable and free of behaviour.
// ── Ports ───────────────────────────────────────────────────────────────────
/**
 * The single output port of a card node.
 * Named rather than implicit so edges are uniform across node types.
 */
export const CARD_PORT_NEXT = 'next';
/** The mandatory fallback port on a conditional node. */
export const CONDITIONAL_PORT_OTHERWISE = 'otherwise';
/** Port id for the nth rule on a conditional node (`rule:0`, `rule:1`, …). */
export function conditionalRulePort(index) {
    return `rule:${index}`;
}
/** Port id for a variant-fork arm. */
export function forkArmPort(armId) {
    return `arm:${armId}`;
}
// Narrowing helpers, colocated with the types so node-kind detection has one
// source of truth (mirrors the isRuleAll/isRuleAny convention in ./rules).
export function isCardNode(node) {
    return node.type === 'card';
}
export function isConditionalNode(node) {
    return node.type === 'conditional';
}
export function isOutcomeNode(node) {
    return node.type === 'outcome';
}
export function isVariantForkNode(node) {
    return node.type === 'variant_fork';
}
/**
 * The output ports a node exposes, in canvas order.
 * Outcome nodes are terminal and expose none.
 */
export function outputPortsOf(node) {
    switch (node.type) {
        case 'card':
            return [CARD_PORT_NEXT];
        case 'conditional':
            return [...node.rules.map((_, i) => conditionalRulePort(i)), CONDITIONAL_PORT_OTHERWISE];
        case 'variant_fork':
            return node.arms.map((arm) => forkArmPort(arm.id));
        case 'outcome':
            return [];
    }
}
